import { t } from "i18next";
import { match, P } from "ts-pattern";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  ethnicitySchema,
  forenameSchema,
  FormSectionSchema,
  IncidentFormTypeSchema,
  LocationSchema,
  longTextSchema,
  mcaGenderSchema,
  nonFutureDateSchema,
  selfDeclaredEthnicitySchema,
  shortTextSchema,
  surnameSchema,
  yesNoSchema,
  yesOnlySchema,
  type CreateIncidentMinimumFields,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "personForename",
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(forenameSchema).build(),
    label: tk("createIncident.personForename"),
    requiredLabel: tk("createIncident.personForenameRequired"),
    defaultValue: ({ incidentWorkItem }) => {
      const createIncidentForm = incidentWorkItem?.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
      );
      const createIncidentSectionData = createIncidentForm?.sections.find(
        (section) => section.id === FormSectionSchema.enum.createIncidentMain,
      )?.data as Partial<CreateIncidentMinimumFields> | undefined;

      const personForename = createIncidentSectionData?.personForename;
      return personForename ? personForename : null;
    },
  })
  .withFieldAlwaysVisible({
    fieldName: "personSurname",
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(surnameSchema).build(),
    label: tk("createIncident.personSurname"),
    requiredLabel: tk("createIncident.personSurnameRequired"),
    defaultValue: ({ incidentWorkItem }) => {
      const createIncidentForm = incidentWorkItem?.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
      );
      const createIncidentSectionData = createIncidentForm?.sections.find(
        (section) => section.id === FormSectionSchema.enum.createIncidentMain,
      )?.data as Partial<CreateIncidentMinimumFields> | undefined;

      const personSurname = createIncidentSectionData?.personSurname;
      return personSurname ? personSurname : null;
    },
  })
  .withFieldAlwaysVisible({
    fieldName: "personAddress",
    type: "location",
    ...new FieldBuilder().withUnknowableSchema(LocationSchema).build(),
    label: tk("personDetails.personAddress"),
    requiredLabel: tk("personDetails.personAddressRequired"),
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmPersonAddress",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("personDetails.confirmPersonAddress"),
    requiredLabel: tk("personDetails.confirmPersonAddressRequired"),
    isVisible: ({ formValues }) =>
      match(formValues.personAddress)
        .with({ value: P._, isUnknown: true }, () => false)
        .with(undefined, { value: P._, isUnknown: false }, () => true)
        .exhaustive(),
  })
  .withFieldAlwaysVisible({
    fieldName: "dateOfBirth",
    type: "date",
    label: tk("personDetails.dob"),
    requiredLabel: tk("personDetails.dobRequired"),
    disableFuture: true,
    ...new FieldBuilder().withUnknowableSchema(nonFutureDateSchema).build(),
  })
  .withFieldConditionallyVisible({
    fieldName: "satisfiedPersonIsOver16",
    type: "radio",
    label: tk("personDetails.satisfiedPersonIsOver16"),
    requiredLabel: tk("personDetails.satisfiedPersonIsOver16Required"),
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
    isVisible: ({ formValues }) =>
      match(formValues.dateOfBirth)
        .with(undefined, { value: P._, isUnknown: false }, () => false)
        .with({ value: P._, isUnknown: true }, () => true)
        .exhaustive(),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "mcaLegalAgeBanner",
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      match(formValues.dateOfBirth)
        .with(undefined, { value: P._, isUnknown: false }, () => false)
        .with({ value: P._, isUnknown: true }, () => true)
        .exhaustive(),
    body: tk("personDetails.mcaLegalAgeBanner"),
  })
  .withFieldAlwaysVisible({
    fieldName: "gender",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(mcaGenderSchema)
      .captureValueLabel()
      .build(),
    label: tk("personDetails.gender.title"),
    requiredLabel: tk("personDetails.gender.required"),
    options: [
      {
        label: tk("personDetails.gender.options.male"),
        value: "male",
      },
      {
        label: tk("personDetails.gender.options.female"),
        value: "female",
      },
      {
        label: tk("personDetails.gender.options.nonBinary"),
        value: "nonBinary",
      },
      {
        label: tk("personDetails.gender.options.preferNotToSay"),
        value: "preferNotToSay",
      },
      {
        label: tk("personDetails.gender.options.other"),
        value: "other",
      },
    ],
  })
  .withFieldAlwaysVisible({
    fieldName: "ethnicAppearance",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(ethnicitySchema)
      .captureValueLabel()
      .build(),
    label: tk("personDetails.ethnicity.title"),
    requiredLabel: tk("personDetails.ethnicity.required"),
    options: [
      {
        label: tk("personDetails.ethnicity.options.IC1"),
        value: "IC1",
      },
      {
        label: tk("personDetails.ethnicity.options.IC2"),
        value: "IC2",
      },
      {
        label: tk("personDetails.ethnicity.options.IC3"),
        value: "IC3",
      },
      {
        label: tk("personDetails.ethnicity.options.IC4"),
        value: "IC4",
      },
      {
        label: tk("personDetails.ethnicity.options.IC5"),
        value: "IC5",
      },
      {
        label: tk("personDetails.ethnicity.options.IC6"),
        value: "IC6",
      },
      {
        label: tk("personDetails.ethnicity.options.IC7"),
        value: "IC7",
      },
    ],
  })
  .withFieldAlwaysVisible({
    fieldName: "selfDeclaredEthnicity",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(selfDeclaredEthnicitySchema)
      .captureValueLabel()
      .build(),
    label: tk("personDetails.selfDeclaredEthnicity.title"),
    requiredLabel: tk("personDetails.selfDeclaredEthnicity.required"),
    options: [
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteEnglish"),
        value: "whiteEnglish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteIrish"),
        value: "whiteIrish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteGypsy"),
        value: "whiteGypsy",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteRoma"),
        value: "whiteRoma",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteOther"),
        value: "whiteOther",
      },
      {
        label: tk(
          "personDetails.selfDeclaredEthnicity.options.mixedBlackCaribbean",
        ),
        value: "mixedBlackCaribbean",
      },
      {
        label: tk(
          "personDetails.selfDeclaredEthnicity.options.mixedBlackAfrican",
        ),
        value: "mixedBlackAfrican",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.mixedAsian"),
        value: "mixedAsian",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.mixedOther"),
        value: "mixedOther",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianEnglish"),
        value: "asianEnglish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianIndian"),
        value: "asianIndian",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianPakistani"),
        value: "asianPakistani",
      },
      {
        label: tk(
          "personDetails.selfDeclaredEthnicity.options.asianBangladeshi",
        ),
        value: "asianBangladeshi",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianChinese"),
        value: "asianChinese",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianOther"),
        value: "asianOther",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackEnglish"),
        value: "blackEnglish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackAfrican"),
        value: "blackAfrican",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackCaribbean"),
        value: "blackCaribbean",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackOther"),
        value: "blackOther",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.arab"),
        value: "arab",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.other"),
        value: "other",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.preferNotToSay"),
        value: "preferNotToSay",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.unableToAnswer"),
        value: "unableToAnswer",
      },
    ],
  })
  .withFieldAlwaysVisible({
    fieldName: "hasRelativeOrFriend",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("personDetails.relativeOrFriend"),
    requiredLabel: tk("personDetails.relativeOrFriendRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "unknownRelativeOrFriend",
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "no",
    body: tk("personDetails.unknownRelativeOrFriend"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendForename",
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(forenameSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendForename"),
    requiredLabel: tk("personDetails.relativeOrFriendForenameRequired"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendSurname",
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(surnameSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendSurname"),
    requiredLabel: tk("personDetails.relativeOrFriendSurnameRequired"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendRelationship",
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("mcaPersonDetails.relativeOrFriendRelationship"),
    requiredLabel: tk("mcaPersonDetails.relativeOrFriendRelationshipRequired"),
    multiline: true,
    rows: "5",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "displayPersonAddress",
    type: "typography",
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    textFn: ({ formValues, t }) =>
      formValues.personAddress.isUnknown
        ? [t("common.unknown")]
        : [formValues.personAddress.value?.userProvided.formattedValue].filter(
            (x) => x !== undefined,
          ),
    label: tk("personDetails.personAddress"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendSameAddress",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("mcaPersonDetails.relativeOrFriendSameAddress"),
    requiredLabel: tk("mcaPersonDetails.relativeOrFriendSameAddressRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendAddress",
    type: "location",
    ...new FieldBuilder().withUnknowableSchema(LocationSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.relativeOrFriendSameAddress?.value === "no",
    label: tk("personDetails.relativeOrFriendAddress"),
    requiredLabel: tk("personDetails.relativeOrFriendAddressRequired"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "unknownRelativeOrFriendAddress",
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      match(formValues.relativeOrFriendAddress)
        .with({ value: P._, isUnknown: true }, () => true)
        .with({ value: P._, isUnknown: false }, () => false)
        .with(null, undefined, () => false)
        .exhaustive(),
    body: tk("personDetails.unknownRelativeOrFriendAddress"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendTelephoneNumber",
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendTelephone"),
    requiredLabel: tk("personDetails.relativeOrFriendTelephoneRequired"),
    inputType: "tel",
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendInformed",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("mcaPersonDetails.relativeOrFriendInformed"),
    requiredLabel: tk("mcaPersonDetails.relativeOrFriendInformedRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "reasonNotInformed",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.relativeOrFriendInformed?.value === "no",
    label: tk("personDetails.reasonNotInformed"),
    requiredLabel: tk("personDetails.reasonNotInformedRequired"),
    multiline: true,
    rows: "5",
  })
  .withPage(["personForename", "personSurname"])
  .withPage(["personAddress", "confirmPersonAddress"])
  .withPage(["dateOfBirth", "satisfiedPersonIsOver16", "mcaLegalAgeBanner"])
  .withPage(["gender"])
  .withPage(["ethnicAppearance", "selfDeclaredEthnicity"])
  .withPage([
    "hasRelativeOrFriend",
    "unknownRelativeOrFriend",
    "relativeOrFriendForename",
    "relativeOrFriendSurname",
    "relativeOrFriendRelationship",
  ])
  .withPage([
    "displayPersonAddress",
    "relativeOrFriendSameAddress",
    "relativeOrFriendAddress",
    "unknownRelativeOrFriendAddress",
  ])
  .withPage([
    "relativeOrFriendTelephoneNumber",
    "relativeOrFriendInformed",
    "reasonNotInformed",
  ])
  .build({ t });

export type McaPersonDetailsFormData = ReturnType<
  typeof formSpecification
>["formType"];
